import { chakra, Flex, Text } from '@chakra-ui/react'
import { FileText, Trash2, X } from 'lucide-react'
import qs from 'query-string'
import { drop } from 'ramda'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import CharacterCount from '~/components/CharacterCounter'
import { Input } from '~/components/core'
import InfiniteList from '~/components/InfiniteList'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  DropdownSelect,
  SelectItem,
} from '~/components/shared/Dropdown/DropdownSelect'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { SnippetOptions } from '~/constants/config'
import { useAnalytics } from '~/hooks/useAnalytics'
import useMaxCharacterCount from '~/hooks/useMaxCharacterCount'
import { useGetPbxList } from '~/queries/pbxQueries'
import {
  MessageSnippetUpsertRequest,
  useDeleteSnippet,
  useGetSnippets,
  useSaveSnippet,
} from '~/queries/snippetQueries'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'
import { Size } from '~/types/ThemeTypes'
import { focusAfterDialogsClose } from '~/utils/focusAfterDialogsClose'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import {
  ApiError,
  MessageSnippet,
  MessageSnippetPostResponse,
  Pbx,
} from '@mangovoice/types'

import {
  CancelButtonWrapper,
  EditBodyInputArea,
  EditContentWrapper,
  EditFooterWrapper,
  EditTitleWrapper,
  EmptyStateWrapper,
  ErrorWrapper,
  FilterMenuWrapper,
  GroupDropdownWrapper,
  HeaderActionWrapper,
  HeaderTitleWrapper,
  HeaderWrapper,
  HelperText,
  ItemActionWrapper,
  ItemNameWrapper,
  ItemWrapper,
  ListWrapper,
  Loading,
  LoadingWrapper,
  RootWrapper,
  ShortcutNameInputWrapper,
  SnippetLabel,
  TextAreaWrapper,
} from './SnippetSettingsStyles'
import FilterMenu from './SnippetsFilter'

interface SnippetForm {
  id?: number | undefined
  name: string
  pbxId?: number | null
  body: string
}
type PbxGroupOption = SelectItem<number | string> & {
  label: string
}
type FocusTarget =
  | { kind: 'newButton' }
  | { kind: 'editor' }
  | { kind: 'snippet'; snippetId: number }

const ErrorTextColor = 'red.subtle'
const BackgroundColor = 'bg.default'
export const SnippetsSettings = () => {
  const { t } = useTranslation()
  const shortcutLabelId = useId()
  const bodyLabelId = useId()
  const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)
  const emptySnippet: SnippetForm = {
    name: '',
    body: '',
  }
  const initialGroupOption: PbxGroupOption = useMemo(
    () => ({
      value: 'personal',
      label: t('snippetsSettings.personal'),
      content: t('snippetsSettings.personal'),
    }),
    [t],
  )
  const [hasNextPage, setHasNextPage] = useState(false)
  const [nextPageOffset, setNextPageOffset] = useState('0')
  const [snippets, setSnippets] = useState<MessageSnippet[]>([])
  const [filter, setFilter] = useState('pbx')
  const [isEditOrNew, setIsEditOrNew] = useState(false)
  const [snippetForm, setSnippetForm] = useState<SnippetForm>(emptySnippet)
  const { pbxId: currentPbxId } = useUserState()
  const [error, setError] = useState<string | null>(null)
  const [pbxList, setPbxList] = useState<Pbx[] | null>([])
  const [groupOptions, setGroupOptions] = useState<PbxGroupOption[]>([
    initialGroupOption,
  ])
  const [pendingFocus, setPendingFocus] = useState<FocusTarget | null>(null)
  const newButtonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()
  const { refetch, isLoading, isFetching } = useGetSnippets(
    currentPbxId,
    nextPageOffset,
    filter === 'personal',
    (data) => {
      setSnippets([...snippets, ...data.results])
      setHasNextPage(data.next !== null)
      if (data.next) {
        const {
          query: { offset },
        } = qs.parseUrl(data.next)
        if (offset && typeof offset === 'string') setNextPageOffset(offset)
      }
    },
    {
      refetchOnWindowFocus: false,
    },
  )
  const handleSnippetDeleted = (_data: unknown, snippetId: number) => {
    track(MessagingAnalyticsEvents.DeleteSnippetSuccess)
    const deletedIndex = snippets.findIndex(
      (snippet) => snippet.id === snippetId,
    )
    const newSnippets = snippets.filter((snippet) => snippet.id !== snippetId)
    setSnippets(newSnippets)
    const nextSnippet =
      deletedIndex >= 0
        ? newSnippets[Math.min(deletedIndex, newSnippets.length - 1)]
        : undefined
    setPendingFocus(
      nextSnippet
        ? { kind: 'snippet', snippetId: nextSnippet.id }
        : { kind: 'newButton' },
    )
  }
  const handleCancelEdit = () => {
    setPendingFocus(
      snippetForm.id !== undefined
        ? { kind: 'snippet', snippetId: snippetForm.id }
        : { kind: 'newButton' },
    )
    setIsEditOrNew(false)
    setSnippetForm(emptySnippet)
  }
  const handleDismissError = () => {
    setError(null)
    setPendingFocus({ kind: 'newButton' })
  }
  const setErrorWithTimeout = (e: string) => {
    setError(e)
    setTimeout(() => {
      setError(null)
    }, 7000)
  }
  const handleSnippetSaved = (response?: MessageSnippetPostResponse) => {
    if (response) {
      track(MessagingAnalyticsEvents.CreateSnippetSuccess)
      if (
        (filter === 'personal' && response.pbx_id === null) ||
        (filter === 'pbx' && response.pbx_id === currentPbxId)
      ) {
        const existingSnippet = snippets.find(
          (snippet) => snippet.id === response.id,
        )
        const newSnippets: MessageSnippet[] = existingSnippet
          ? snippets.map((snippet) => {
              if (snippet.id === response.id) {
                return {
                  id: response.id,
                  name: response.name,
                  body: response.body,
                  pbx: response.pbx_id
                    ? { id: response.pbx_id, name: '' }
                    : undefined,
                  scope: response.scope,
                  created_at: response.created_at,
                  updated_at: response.updated_at,
                  created_by: {
                    full_name: '',
                    id: 0,
                  },
                  last_updated_by: {
                    full_name: '',
                    id: 0,
                  },
                }
              }
              return snippet
            })
          : [
              {
                id: response.id,
                name: response.name,
                body: response.body,
                pbx: response.pbx_id
                  ? { id: response.pbx_id, name: '' }
                  : undefined,
                scope: response.scope,
                created_at: response.created_at,
                updated_at: response.updated_at,
                created_by: {
                  full_name: '',
                  id: 0,
                },
                last_updated_by: {
                  full_name: '',
                  id: 0,
                },
              },
              ...snippets,
            ]
        setSnippets(newSnippets)
      } else if (response.pbx_id !== currentPbxId && filter === 'pbx') {
        const newSnippets = snippets.filter(
          (snippet) => snippet.pbx?.id === currentPbxId,
        )
        setSnippets(newSnippets)
      }
    }
  }
  const handleSnippetSaveFailed = (errorResponse: ApiError) => {
    track(MessagingAnalyticsEvents.CreateSnippetFailure, {
      error: errorResponse.message,
    })
    setErrorWithTimeout(
      errorResponse.message ?? t('snippetsSettings.errorWithTimeout'),
    )
  }
  const deleteMutation = useDeleteSnippet(handleSnippetDeleted)
  const saveMutation = useSaveSnippet(
    handleSnippetSaved,
    handleSnippetSaveFailed,
  )
  const handleSaveSnippet = () => {
    const { id, body, name, pbxId } = snippetForm
    const payload: MessageSnippetUpsertRequest = {
      name: `#${name}`,
      body,
      pbx_id: pbxId ?? null,
      message_group_id: null,
      id: id,
    }
    saveMutation.mutate(payload)
    handleCancelEdit()
  }
  useGetPbxList({
    onQuerySuccess: (data) => {
      setPbxList(data.results)
    },
    apiParams: {
      limit: 300,
    },
  })
  const { count, hasExceededMaxCount } = useMaxCharacterCount(
    snippetForm.body.length,
  )
  useEffect(() => {
    if (pbxList) {
      setGroupOptions(() => {
        const pbxGroups: PbxGroupOption[] = pbxList.map(({ id, name }) => ({
          value: id,
          label: name,
          content: name,
        }))
        return [{ ...initialGroupOption }, ...pbxGroups]
      })
    }
  }, [initialGroupOption, pbxList])
  const handleAddNew = () => {
    setIsEditOrNew(true)
    setSnippetForm(emptySnippet)
    setPendingFocus({ kind: 'editor' })
  }
  const handleChangeSnippetName = (name: string) => {
    setSnippetForm((prevValues) => ({ ...prevValues, name }))
  }
  const handleChangeSnippetBody = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const str = e.target.value as string
    setSnippetForm((prevValues) => ({
      ...prevValues,
      body: str.replace(/(<([^>]+)>)/gi, ''),
    }))
  }
  const handleSelectedGroup = (selectedValue: number | string) => {
    setSnippetForm((prevValues) => ({
      ...prevValues,
      pbxId:
        selectedValue === 'personal' || typeof selectedValue === 'string'
          ? null
          : selectedValue,
    }))
  }
  const handleEditSnippet = (snippet: MessageSnippet) => {
    const { id, pbx, name, body } = snippet
    const cleanName = drop(1, name)
    setSnippetForm({
      id,
      body,
      name: cleanName,
      pbxId: pbx?.id ?? null,
    })
    setIsEditOrNew(true)
    setPendingFocus({ kind: 'editor' })
  }
  const handleDeleteSnippet = (snippet: MessageSnippet) => {
    setConfirmModalState({
      title: t('snippetsSettings.confirmationModal.title'),
      message: t('snippetsSettings.confirmationModal.message'),
      confirmText: t('snippetsSettings.confirmationModal.confirmText'),
      cancelText: t('snippetsSettings.confirmationModal.cancelText'),
      isDestructive: true,
      onConfirm: () => deleteMutation.mutate(snippet.id),
    })
  }
  const handleSelectedFilter = (selectedOption: string) => {
    if (selectedOption !== filter) {
      setFilter(selectedOption)
      setNextPageOffset('0')
      setSnippets([])
    }
  }
  const { name, body } = snippetForm
  const selectedValue = snippetForm.pbxId ?? 'personal'
  const showEditing = isEditOrNew
  const showLoading =
    !showEditing && (isLoading || (isFetching && snippets.length === 0))
  const showList = !showEditing && !showLoading && snippets.length > 0
  const showEmpty = !showEditing && !showLoading && !showList

  useEffect(() => {
    if (!pendingFocus) return
    const getNewButton = () => newButtonRef.current
    return focusAfterDialogsClose(
      () => {
        const container = containerRef.current
        switch (pendingFocus.kind) {
          case 'newButton':
            return getNewButton()
          case 'editor':
            return container?.querySelector<HTMLElement>(
              `input[aria-labelledby="${shortcutLabelId}"]`,
            )
          case 'snippet':
            return container?.querySelector<HTMLElement>(
              `[data-snippet-id="${pendingFocus.snippetId}"]`,
            )
        }
      },
      { fallback: getNewButton },
    )
  }, [pendingFocus, shortcutLabelId])

  return (
    <Flex
      ref={containerRef}
      width="100%"
      backgroundColor="bg.subtle"
      height="100%"
    >
      <RootWrapper>
        {error && (
          <ErrorWrapper role="alert">
            <Text
              {...textStyles[textStyleKeys.body2Semibold]}
              color={ErrorTextColor}
            >
              {error}
            </Text>
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.xs}
              actionIcon={X}
              tooltipText={t('snippetsSettings.dismissError')}
              onClick={handleDismissError}
              flexShrink={0}
            />
          </ErrorWrapper>
        )}
        <HeaderWrapper>
          <HeaderTitleWrapper>
            {showEditing ? (
              <EditTitleWrapper>
                <Text {...textStyles[textStyleKeys.Body2]} color="fg.default">
                  {snippetForm.id
                    ? t('snippetsSettings.editSnippet')
                    : t('snippetsSettings.newSnippet')}
                </Text>
              </EditTitleWrapper>
            ) : (
              <>
                <FileText size={24} />
                <Text
                  {...textStyles[textStyleKeys.Body2]}
                  ml="0.5rem"
                  color="fg.default"
                >
                  {t('snippetsSettings.headerText')}
                </Text>
              </>
            )}
          </HeaderTitleWrapper>
          <HeaderActionWrapper>
            {showEditing ? (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                size={Size.sm}
                actionIcon={X}
                tooltipText={t('snippetsSettings.cancelEditing')}
                onClick={() => handleCancelEdit()}
              />
            ) : (
              <MangoButton
                ref={newButtonRef}
                onClick={() => handleAddNew()}
                size={Size.sm}
              >
                {t('snippetsSettings.newButtonText')}
              </MangoButton>
            )}
          </HeaderActionWrapper>
        </HeaderWrapper>
        {!showEditing && (
          <FilterMenuWrapper>
            <FilterMenu
              selectedFilter={filter}
              options={SnippetOptions()}
              ariaLabel={t('a11y.filterGroup.snippets')}
              onChange={handleSelectedFilter}
            />
          </FilterMenuWrapper>
        )}
        <ListWrapper>
          {showList && (
            <InfiniteList
              style={{ backgroundColor: BackgroundColor }}
              data={snippets}
              onEndReached={() => (hasNextPage ? refetch() : null)}
              listItem={(_, snippet) => {
                return (
                  <ItemWrapper key={`snippet-${snippet.id}`}>
                    <ItemNameWrapper asChild>
                      <chakra.button
                        type="button"
                        data-snippet-id={snippet.id}
                        aria-label={t('snippetsSettings.editSnippetLabel', {
                          name: snippet.name,
                        })}
                        onClick={() => handleEditSnippet(snippet)}
                        textAlign="start"
                        cursor="pointer"
                        borderRadius="sm"
                      >
                        <Text
                          as="span"
                          {...textStyles[textStyleKeys.Body4]}
                          color="fg.default"
                        >
                          {snippet.name}
                        </Text>
                      </chakra.button>
                    </ItemNameWrapper>
                    <ItemActionWrapper>
                      <QuickActionButton
                        variant={QuickActionButtonVariants.Default_100}
                        size={Size.xs}
                        actionIcon={Trash2}
                        tooltipText={t('snippetsSettings.tooltipDelete')}
                        ariaLabel={t('snippetsSettings.deleteSnippetLabel', {
                          name: snippet.name,
                        })}
                        onClick={() => handleDeleteSnippet(snippet)}
                      />
                    </ItemActionWrapper>
                  </ItemWrapper>
                )
              }}
            />
          )}
          {showEditing && (
            <>
              <EditContentWrapper paddingBottom="1rem">
                <ShortcutNameInputWrapper>
                  <SnippetLabel>
                    <Text
                      id={shortcutLabelId}
                      {...textStyles[textStyleKeys.Body4_Bold]}
                      color="fg.default"
                    >
                      {t('snippetsSettings.shortcut')}
                    </Text>
                  </SnippetLabel>
                  <Input
                    aria-labelledby={shortcutLabelId}
                    prefix="#"
                    placeholder={t('snippetsSettings.inputPlaceholder')}
                    value={name}
                    onChange={handleChangeSnippetName}
                  />
                </ShortcutNameInputWrapper>
                <HelperText>
                  <Text {...textStyles[textStyleKeys.Body4]} color="fg.subtle">
                    {t('snippetsSettings.shortcutGuideText')}
                  </Text>
                </HelperText>
                <GroupDropdownWrapper>
                  <SnippetLabel alignItems={'flex-start'}>
                    <Text
                      {...textStyles[textStyleKeys.Body4_Bold]}
                      textAlign="left"
                      color="fg.default"
                    >
                      {t('snippetsSettings.visibility')}
                    </Text>
                  </SnippetLabel>
                  <DropdownSelect
                    items={groupOptions}
                    selectedValue={selectedValue}
                    onSelect={handleSelectedGroup}
                  />
                </GroupDropdownWrapper>
                <HelperText>
                  <Text {...textStyles[textStyleKeys.Body4]} color="fg.subtle">
                    {t('snippetsSettings.visibilityGuideText')}
                  </Text>
                </HelperText>
                <SnippetLabel>
                  <Text
                    id={bodyLabelId}
                    {...textStyles[textStyleKeys.Body4_Bold]}
                    color="fg.default"
                  >
                    {t('snippetsSettings.body')}
                  </Text>
                </SnippetLabel>
                <TextAreaWrapper>
                  <EditBodyInputArea
                    aria-labelledby={bodyLabelId}
                    hasExceededMaxCount={hasExceededMaxCount}
                    placeholder={t('snippetsSettings.editBodyInputPlaceholder')}
                    value={body}
                    onChange={handleChangeSnippetBody}
                  />
                  <CharacterCount count={count} />
                </TextAreaWrapper>
              </EditContentWrapper>
              <EditFooterWrapper marginTop="1rem">
                <CancelButtonWrapper>
                  <MangoButton
                    size={Size.sm}
                    variant={MangoButtonVariant.Default_200}
                    onClick={() => handleCancelEdit()}
                  >
                    {t('snippetsSettings.mangoButtonCancel')}
                  </MangoButton>
                </CancelButtonWrapper>
                <MangoButton
                  disabled={name === '' || body === '' || hasExceededMaxCount}
                  onClick={() => handleSaveSnippet()}
                  size={Size.sm}
                >
                  {t('snippetsSettings.mangoButtonSave')}
                </MangoButton>
              </EditFooterWrapper>
            </>
          )}
          {showLoading && (
            <LoadingWrapper
              style={{
                width: '100%',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <Loading>{t('snippetsSettings.loadingSnippets')}</Loading>
            </LoadingWrapper>
          )}
          {showEmpty && (
            <EmptyStateWrapper>
              <Text {...textStyles[textStyleKeys.Body1]}>
                {t('snippetsSettings.noSnippetsFound')}
              </Text>
            </EmptyStateWrapper>
          )}
        </ListWrapper>
      </RootWrapper>
    </Flex>
  )
}
export default SnippetsSettings
